import React, { Component } from "react";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { ConnectionError } from "@formant/core/common/ConnectionError";
import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { parseIsoDate } from "@formant/date-fns/parseIsoDate";
import { IQuery } from "@formant/core/model/IQuery";
import { IValidation } from "@formant/core/model/IValidation";
import { animate } from "@formant/ui/utils/animate";

import { observeStore, organizationStore, queryStore } from "../../stores";
import { Buttons } from "../common/Buttons";
import { DateTimeField } from "../common/DateTimeField";
import { Escape } from "../common/Escape";
import { Form } from "../common/Form";
import { Heading3 } from "../common/Heading3";
import { Popup } from "../common/Popup";
import { RadioGroup } from "../common/RadioGroup";
import { StandardButton } from "../common/StandardButton";
import { ValidationMessages } from "../common/ValidationMessages";
import { ErrorPageError } from "../ErrorPageError";
import * as styles from "./ExportTelemetryPopup.scss";

export interface IExportTelemetryPopupProps {
    query: IQuery;
}

@observer
export class ExportTelemetryPopup extends Component<IExportTelemetryPopupProps> {
    @observable
    private validation: IValidation | undefined;

    @observable
    private loading: boolean = false;

    @observable
    private visible: boolean = true;

    @observable
    private startTime: Date = parseIsoDate(this.props.query.start);

    @observable
    private endTime: Date = parseIsoDate(this.props.query.end);

    @observable
    private exportType: "Direct download" | "Google sheets" = "Direct download";

    public render() {
        const {
            validation,
            loading,
            visible,
            startTime,
            endTime,
            onSubmit,
            onLoading,
            onChangeEndTime,
            onChangeStartTime,
            onChangeExportType,
            onClose,
        } = this;

        if (!organizationStore.dataExportEnabled()) {
            return false;
        }

        return (
            <Popup
                visible={visible}
                onClose={onClose}
                className={styles["export-telemetry-popup"]}
            >
                <Form onSubmit={onSubmit} onLoading={onLoading}>
                    <Escape onEsc={onClose} />
                    <Heading3 className={styles.heading}>
                        Export Telemetry
                    </Heading3>
                    <p>Export Formant telemetry data.</p>
                    <DateTimeField
                        label="Start time"
                        time={startTime}
                        onTimeChanged={onChangeStartTime}
                        disabled={loading}
                    />
                    <DateTimeField
                        label="End time"
                        time={endTime}
                        onTimeChanged={onChangeEndTime}
                        disabled={loading}
                    />
                    <ValidationMessages
                        className={styles.validation}
                        validation={validation}
                    />
                    {organizationStore.isGoogleSheetsEnabled && (
                        <RadioGroup
                            value={this.exportType}
                            options={
                                organizationStore.dataExportEnabled()
                                    ? [
                                          { value: "Direct download" },
                                          { value: "Google sheets" },
                                      ]
                                    : [{ value: "Direct download" }]
                            }
                            onChange={onChangeExportType}
                            data-testid="export-radiobutton"
                        />
                    )}
                    <Buttons className={styles.buttons}>
                        <StandardButton
                            id="close-button"
                            onClick={onClose}
                            type="button"
                            data-testid="cancel-button"
                        >
                            Cancel
                        </StandardButton>
                        <StandardButton
                            primary={true}
                            loading={loading}
                            data-testid="export-button"
                        >
                            Export
                        </StandardButton>
                    </Buttons>
                </Form>
            </Popup>
        );
    }

    private onSubmit = async () => {
        const query: IQuery = {
            ...this.props.query,
            start: this.startTime.toISOString(),
            end: this.endTime.toISOString(),
        };
        this.validation = undefined;
        try {
            if (this.exportType === "Google sheets") {
                await queryStore.exportSheet(query);
            } else {
                await queryStore.export(query);
            }
            await this.onClose();
        } catch (error) {
            if (error instanceof ConnectionError) {
                this.validation = {
                    other: ["Network request failed. Please try again."],
                };
                return;
            }
            const responseError = narrowError(error, ResponseError);
            if (responseError.statusCode === 422) {
                this.validation = responseError.validationErrors;
            } else if (this.exportType === "Google sheets") {
                throw new ErrorPageError(
                    error as Error,
                    "Error exporting telemetry data. Please try to disconnect and reconnect Google Sheets integration.",
                );
            } else {
                throw error;
            }
        }
    };

    private onClose = async () => {
        this.visible = false;
        await animate();
        observeStore.exportTelemetryPopupQuery = undefined;
    };

    private onLoading = (loading: boolean) => {
        this.loading = loading;
    };

    private onChangeStartTime = (time: Date): void => {
        this.startTime = time;
    };

    private onChangeEndTime = (time: Date): void => {
        this.endTime = time;
    };

    private onChangeExportType = (value: string): void => {
        if (value === "Google sheets") {
            this.exportType = "Google sheets";
        } else {
            this.exportType = "Direct download";
        }
    };
}
